import { Flex, Menu, Switch, TextField, ToggleButton } from 'components'
import { MediaInput } from 'components/media'
import { useWorkspace } from 'components/providers'
import { startCase } from 'lodash-es'
import {
  ITrait,
  PrimaryLeadProperty,
  RadioLineParams as RadioLineParamsType,
  TraitDisplayTypes,
  TraitWrapper,
} from 'types'
import { hideTraitValues, leadTraitProperties } from '../../utils'

interface Props {
  value: TraitWrapper
  onChange: (value: TraitWrapper) => void
}

export default function ManageTrait({ value, onChange }: Props) {
  const { workspace } = useWorkspace()

  function update(updates: Partial<ITrait>) {
    onChange({ ...value, ...updates })
  }

  const {
    alphaScroll,
    control,
    displayType = 'grid',
    helpText,
    isMulti,
    leadField,
    learnMore,
    media,
    radioLineParams,
    showFrequentSelections,
    title,
    titleDisplay,
  } = value

  const workspaceProperties = workspace?.properties || []
  const leadFieldOptions = [
    ...leadTraitProperties.map(p => ({ value: p, label: startCase(p) })),
    ...workspaceProperties.map(p => ({ value: p.title, label: p.title })),
  ]

  return (
    <Flex direction="column" gap={4}>
      <TextField
        label="Title"
        required
        value={title?.en || ''}
        onChange={e => update({ title: { ...title, en: e.target.value } })}
        placeholder="Enter title"
      />

      <TextField
        label="Display Title"
        value={titleDisplay?.en || title?.en || ''}
        onChange={e => {
          const en = e.target.value
          const isSame = en === title?.en
          update({ titleDisplay: isSame ? undefined : { ...titleDisplay, en } })
        }}
        placeholder="Override title in Form step"
      />

      <TextField
        label="Help Text"
        value={helpText?.en || ''}
        onChange={e => update({ helpText: { ...helpText, en: e.target.value } })}
        placeholder="Override default help message"
      />

      <Menu
        label="Lead Field"
        options={leadFieldOptions}
        value={(leadField as PrimaryLeadProperty) || ''}
        onChange={leadField => update({ leadField: leadField || undefined })}
        placeholder="Field to set on lead instead of trait"
      />

      <MediaInput
        label="Background Media"
        type="image"
        id={media}
        onSelect={media => update({ media })}
        onClear={() => update({ media: undefined })}
      />

      <ToggleButton
        label="Display Type"
        alignSelf="flex-start"
        value={displayType}
        options={displayTypeOptions}
        onChange={displayType => update({ displayType })}
      />

      <Switch
        label="Control Background"
        checked={control === 'background'}
        onChange={checked => update({ control: checked ? 'background' : undefined })}
      />

      {!hideTraitValues(leadField) && (
        <Switch label="Multi-select" checked={isMulti} onChange={isMulti => update({ isMulti })} />
      )}

      {displayType === 'list' && (
        <Switch
          label="Alpha Scroll"
          checked={alphaScroll || false}
          onChange={alphaScroll => update({ alphaScroll })}
        />
      )}

      {displayType === 'grid' && (
        <Switch
          label="Learn More"
          checked={learnMore || false}
          onChange={learnMore => update({ learnMore })}
        />
      )}

      <Switch
        label="Show Frequent Selections"
        checked={showFrequentSelections || false}
        onChange={showFrequentSelections => update({ showFrequentSelections })}
      />

      {displayType === 'radio-line' && (
        <RadioLineParams
          value={radioLineParams}
          onChange={radioLineParams => update({ radioLineParams })}
        />
      )}
    </Flex>
  )
}

const displayTypeOptions: { value: TraitDisplayTypes; label: string }[] = [
  { value: 'grid', label: 'Grid' },
  { value: 'list', label: 'List' },
  { value: 'select', label: 'Select' },
  { value: 'radio-line', label: 'Radio Line' },
]

interface RadioLineParamsProps {
  value?: RadioLineParamsType
  onChange: (value: RadioLineParamsType) => void
}

function RadioLineParams({ value = defaultRadioParams, onChange }: RadioLineParamsProps) {
  function update(updates: Partial<RadioLineParamsType>) {
    onChange({ ...value, ...updates })
  }

  const { minValue, maxValue, minValueText, maxValueText, step } = value

  return (
    <>
      <TextField
        label="Min Value"
        required
        type="number"
        value={minValue.toString()}
        onChange={e => update({ minValue: Number(e.target.value) })}
      />

      <TextField
        label="Max Value"
        required
        type="number"
        value={maxValue.toString()}
        onChange={e => update({ maxValue: Number(e.target.value) })}
      />

      <TextField
        label="Step"
        type="number"
        value={(step || 1).toString()}
        onChange={e => update({ step: Number(e.target.value) })}
      />

      <TextField
        label="Min Value Text"
        value={minValueText?.en || ''}
        onChange={e =>
          update({
            minValueText: {
              ...minValueText,
              en: e.target.value,
            },
          })
        }
      />

      <TextField
        label="Max Value Text"
        value={maxValueText?.en || ''}
        onChange={e =>
          update({
            maxValueText: {
              ...maxValueText,
              en: e.target.value,
            },
          })
        }
      />
    </>
  )
}

const defaultRadioParams: RadioLineParamsType = { minValue: 0, maxValue: 100 }
